/**
 * Weyermann Specialty Malts — Bamberg, Germany.
 *
 * Colour is published in EBC; the °L figure alongside it is the conversion
 * `°L = (EBC + 1.2) / 2.65` rounded to one decimal, not a second measurement.
 * Diastatic power is published in Windisch-Kolbach and converted with
 * `°Lintner = (°WK + 16) / 3.5`, which is why the numbers look low next to a
 * US 2-row.
 *
 * Two rows here are not German. Weyermann's floor-malted Bohemian pair is
 * malted in Bohemia — their own specification says `Source: Bohemia` — so the
 * origin follows the malthouse rather than the letterhead, and those two sit
 * alongside the Czech maltsters in a search by origin, which is where a brewer
 * hunting a terroir pilsner malt would look for them. CARABOHEMIAN is named
 * for the style and not the country: its specification says
 * `Source: Germany, EU`, so it stays German. Neither floor-malted row carries
 * a Windisch-Kolbach figure — Weyermann grade those two by words ("high",
 * "medium") rather than by number — so their diastatic power is class-typical
 * and they are marked `representative`.
 */

import type { Fermentable } from '../../schema/fermentable';

/** Weyermann malts. @see Fermentable */
export const weyermannFermentables: readonly Fermentable[] = [
  {
    id: 'weyermann-cararye',
    name: 'Cararye',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 66.5,
    colorEbc: 175,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "German rye caramelized rather than kilned, and the one caramel malt here that is not barley. Bread, coffee, dark chocolate and dried fruit, with rye's own pepper underneath. The color band is the maker's; extract and moisture are not published for it and are class-typical for a caramel malt of this depth.",
    tags: ['crystal', 'caramel', 'rye', 'german', 'dark'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-cararye/',
    provenance: 'representative',
  },
  {
    id: 'weyermann-cologne',
    name: 'Cologne Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.5,
    moisturePercent: 5.5,
    colorLovibond: 3.5,
    colorEbc: 8,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sold as Brewing Malt Type Cologne, and made for the beer it is named after: pale gold, medium-light in body, with malty-sweet honey and biscuit notes. Brewable as the whole grist for Koelsch, and useful anywhere a pale ale wants a continental base rather than an English one. No Windisch-Kolbach figure is published, so the diastatic power is class-typical.',
    tags: ['base', 'koelsch', 'german', 'pale'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-brewing-malt-type-cologne/',
    provenance: 'representative',
  },
  {
    id: 'weyermann-eraclea-pilsner',
    name: 'Eraclea Pilsner Malt',
    maker: 'Weyermann',
    origin: 'Italy',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.5,
    moisturePercent: 5,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Italian barley grown on the Adriatic plain north of Venice and malted in Bamberg, and the base an Italian pilsner is built on. Malty-sweet with a light honey note, pale enough to brew as the whole grist. Weyermann grade its enzymes 'high' in words and publish no Windisch-Kolbach figure, so the diastatic power here is class-typical.",
    tags: ['base', 'pilsner', 'italian', 'lager', 'terroir'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-eraclea-pilsner-malt/',
    provenance: 'representative',
  },
  {
    id: 'weyermann-extra-pale-premium-pilsner',
    name: 'Extra Pale Premium Pilsner Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.5,
    moisturePercent: 5,
    colorLovibond: 1.5,
    colorEbc: 2.75,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Paler than Weyermann's standard pilsner malt and made for beers that have to look almost white in the glass. Malty-sweet with light honey, brewable as the whole grist. Enzymes are graded 'high' in words with no Windisch-Kolbach figure, so the diastatic power here is class-typical.",
    tags: ['base', 'pilsner', 'german', 'pale', 'lager'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-extra-pale-premium-pilsner-malt/',
    provenance: 'representative',
  },
  {
    id: 'weyermann-isaria-1924',
    name: 'Isaria 1924',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 3.5,
    colorEbc: 8,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "The oldest German malting barley still malted commercially, approved for brewing in 1924 and revived as a heirloom line. Malty-sweet with a soft biscuit aroma and a rounder mouthfeel than modern two-row, which is the point of brewing a Zoigl or a Kellerbier with it. Enzymes are graded 'medium' rather than numbered, so the diastatic power is class-typical.",
    tags: ['base', 'heirloom', 'german', 'landrace', 'lager'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-isaria-1924-3/',
    provenance: 'representative',
  },
  {
    id: 'weyermann-pilsner',
    name: 'Pilsner Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.3,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: 5.75,
    bufferingCapacity: null,
    description:
      'The reference German pilsner malt: pale, clean and lightly sweet, with plenty of enzyme left over to convert adjuncts. Works as the whole grist for any pale lager and as a neutral canvas under big hop loads.',
    tags: ['base', 'lager', 'german', 'pilsner'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-pilsner-malt-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-bohemian-pilsner',
    name: 'Bohemian Pilsner Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: 5.75,
    bufferingCapacity: null,
    description:
      'Floor-influenced pilsner malt made from Czech-style barley, kilned a shade darker than the standard product. Gives Czech pale lagers their fuller, bready malt spine without adding real color.',
    tags: ['base', 'lager', 'german', 'pilsner', 'czech'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-bohemian-pilsner-malt-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-floor-malted-bohemian-pilsner',
    name: 'Floor-Malted Bohemian Pilsner Malt',
    maker: 'Weyermann',
    origin: 'Czech Republic',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 5.5,
    colorLovibond: 1.9,
    colorEbc: 3.75,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Bojos and Malz barley floor-malted in Bohemia rather than at Bamberg, and the difference is the point: honey and biscuit over a malt aroma far louder than the extract figure suggests. The whole grist of a světlý ležák, and worth a step mash to reward it.',
    tags: ['base', 'lager', 'pilsner', 'czech', 'bohemian', 'floor-malted', 'heritage'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-floor-malted-bohemian-pilsner-malt-2/',
    provenance: 'representative',
  },
  {
    id: 'weyermann-floor-malted-bohemian-dark',
    name: 'Floor-Malted Bohemian Dark Malt',
    maker: 'Weyermann',
    origin: 'Czech Republic',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 6.5,
    colorEbc: 16,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The same Bohemian floor malting taken to Munich color: toffee and dark caramel, a full body and a frank malt sweetness, with enough enzyme left to stand as the whole grist. The malt a tmavé ležák or a Bohemian bock is built out of rather than seasoned with.',
    tags: ['base', 'kilned', 'munich', 'dark', 'czech', 'bohemian', 'floor-malted'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-floor-malted-bohemian-dark-malt-2-2/',
    provenance: 'representative',
  },
  {
    id: 'weyermann-carabohemian',
    name: 'CARABOHEMIAN',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73,
    moisturePercent: 6.5,
    colorLovibond: 74,
    colorEbc: 195,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Caramelization pushed harder than the CARAMUNICH grades for a deeper color and a darker sugar: bread crust, caramel and toffee. Named for the styles it was built for rather than for where it is made, which is Bamberg.',
    tags: ['crystal', 'caramel', 'dark', 'czech', 'bohemian', 'malty'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-carabohemian-5/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-barke-pilsner',
    name: 'Barke Pilsner Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.5,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: 5.75,
    bufferingCapacity: null,
    description:
      'Made from the heritage Barke barley variety, which yields less than modern cultivars but is prized for a rounder, more expressive malt flavor. A straight swap for standard pilsner malt when the beer is all about the malt.',
    tags: ['base', 'lager', 'german', 'heritage'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-barke-pilsner-malt-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-pale-ale',
    name: 'Pale Ale Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 2.9,
    colorEbc: 6.5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: 5.72,
    bufferingCapacity: null,
    description:
      'German pale ale malt, kilned warmer than pilsner for a fuller, slightly biscuity base. The default choice when a continental grist wants more malt presence than pilsner gives but no color to speak of.',
    tags: ['base', 'ale', 'german'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-pale-ale-malt-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-spelt',
    name: 'Spelt Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 79.5,
    moisturePercent: 6,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "German spelt malted rather than barley, and it drinks softer and nuttier than wheat does. Protein runs to 17 %, which is high even for an ancient grain, so it builds body and head; husk-free, so plan the lauter above about 40 %. Enzymes are graded 'high' without a number, so the diastatic power is class-typical.",
    tags: ['spelt', 'german', 'ancient-grain', 'nutty', 'multigrain'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-spelt-malt/',
    provenance: 'representative',
  },
  {
    id: 'weyermann-vienna',
    name: 'Vienna Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 3.3,
    colorEbc: 7.5,
    diastaticPowerLintner: 67,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: 5.7,
    bufferingCapacity: null,
    description:
      'Lightly kilned amber malt with enough enzyme to stand alone as a base. Contributes a golden color and a toasted-bread sweetness that defines Vienna lager and rounds out Märzen.',
    tags: ['base', 'kilned', 'german', 'vienna'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-vienna-malt-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-munich-type-1',
    name: 'Munich Malt Type I',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.5,
    colorLovibond: 5.7,
    colorEbc: 14,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: 5.65,
    bufferingCapacity: null,
    description:
      'The lighter of the two Munich malts, still self-converting. Adds a deep golden color and a rich bready malt character; usable as an entire grist for dunkel and bock or as a 10–30 % seasoning elsewhere.',
    tags: ['base', 'kilned', 'german', 'munich'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-munich-malt-type-1-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-munich-type-2',
    name: 'Munich Malt Type II',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.5,
    moisturePercent: 4.5,
    colorLovibond: 8.8,
    colorEbc: 22,
    diastaticPowerLintner: 42,
    suggestedMaxPercent: 80,
    needsMashing: true,
    diPh: 5.6,
    bufferingCapacity: null,
    description:
      'Darker Munich with a pronounced toasted crust flavor and just enough enzyme to convert itself. Use it where a dunkel or doppelbock needs depth without reaching for crystal malt.',
    tags: ['base', 'kilned', 'german', 'munich', 'dark'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-munich-malt-type-2-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-abbey',
    name: 'Abbey Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 19,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A high-kilned specialty malt aimed at Belgian abbey beers, sitting between dark Munich and a light crystal in character. Brings dried-fruit and honeyed-bread notes at modest percentages.',
    tags: ['kilned', 'german', 'belgian-style'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-abbey-malt-4/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-melanoidin',
    name: 'Melanoidin Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 26.9,
    colorEbc: 70,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Intensely stewed then kilned to force Maillard development, mimicking the flavor a decoction mash builds. A little goes a long way: 3–8 % deepens color and adds a malty, faintly honeyed richness.',
    tags: ['kilned', 'german', 'decoction'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-melanoidin-malt-6/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-carafoam',
    name: 'Carafoam',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 6,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dextrin malt sold as the Weyermann equivalent of carapils. Adds body, mouthfeel and head retention without color or perceptible sweetness; 3–10 % is plenty.',
    tags: ['dextrin', 'german', 'body', 'head-retention'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-carafoam/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-carahell',
    name: 'Carahell',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 9.9,
    colorEbc: 25,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest of the Weyermann caramel malts. Lifts body and adds a gentle honey sweetness to helles, weissbier and pale ales without shifting the color much past straw.',
    tags: ['crystal', 'caramel', 'german', 'light'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-carahell-4/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-carared',
    name: 'Carared',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Caramel malt selected for the red-amber hue it throws rather than for sweetness. The go-to for putting a copper glow into an altbier, Märzen or red ale at 5–10 %.',
    tags: ['crystal', 'caramel', 'german', 'red'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-carared-5/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-caramunich-type-1',
    name: 'Caramunich Type I',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 34.4,
    colorEbc: 90,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Medium caramel malt with a clean toffee sweetness and an amber cast. The lightest of the three Caramunich grades and the most forgiving in pale beers.',
    tags: ['crystal', 'caramel', 'german'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-caramunich-type-1-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-caramunich-type-2',
    name: 'Caramunich Type II',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 45.7,
    colorEbc: 120,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle Caramunich grade and the most widely used: rich caramel and raisin notes with a deep amber color. Standard seasoning for bock, dunkel and amber ales at 3–10 %.',
    tags: ['crystal', 'caramel', 'german'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-caramunich-type-2-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-caramunich-type-3',
    name: 'Caramunich Type III',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest Caramunich, leaning towards dried fruit, burnt sugar and a firm caramel bitterness. Use sparingly in dark lagers and Belgian dubbels.',
    tags: ['crystal', 'caramel', 'german', 'dark'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-caramunich-type-3-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-caraaroma',
    name: 'Caraaroma',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 123,
    colorEbc: 325,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A very dark caramel malt that behaves halfway between crystal and roast. Adds intense raisin and dark-fruit depth plus a red-brown color; 2–5 % is usually the limit before it turns astringent.',
    tags: ['crystal', 'caramel', 'german', 'dark'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-caraaroma-5/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-carafa-type-1',
    name: 'Carafa Type I',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 321,
    colorEbc: 850,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The mildest Weyermann roasted malt, husks intact. Contributes chocolate and light coffee notes with far less harshness than black malt at the same color.',
    tags: ['roasted', 'german', 'chocolate'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-carafa-type-1-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-carafa-type-2',
    name: 'Carafa Type II',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 434,
    colorEbc: 1150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 6,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Mid-grade roasted malt with clear coffee and dark chocolate character. The usual choice for putting real roast into a porter or schwarzbier.',
    tags: ['roasted', 'german', 'coffee'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-carafa-type-2-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-carafa-type-3',
    name: 'Carafa Type III',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 529,
    colorEbc: 1400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest standard Carafa: espresso, bitter chocolate and a firm roast bite. Small additions blacken a beer quickly, so treat 2–4 % as the working range.',
    tags: ['roasted', 'german', 'black'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-carafa-type-3-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-carafa-special-type-1',
    name: 'Carafa Special Type I',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 321,
    colorEbc: 850,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dehusked version of Carafa Type I. Removing the husk strips out most of the tannin harshness, which is why it is the standard tool for coloring a beer without adding astringency.',
    tags: ['roasted', 'german', 'dehusked', 'smooth'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-carafa-special-type-1-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-carafa-special-type-2',
    name: 'Carafa Special Type II',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 434,
    colorEbc: 1150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 6,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dehusked mid-grade roast: smooth chocolate and coffee with none of the scratchiness of husked black malt. The classic late-mash addition for schwarzbier.',
    tags: ['roasted', 'german', 'dehusked', 'coffee'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-carafa-special-type-2-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-carafa-special-type-3',
    name: 'Carafa Special Type III',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 529,
    colorEbc: 1400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest dehusked Carafa. Delivers jet-black color and deep roast without the tannic edge, making it the preferred way to darken a black IPA.',
    tags: ['roasted', 'german', 'dehusked', 'black'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-carafa-special-type-3-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-acidulated',
    name: 'Acidulated Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'base',
    maltClass: 'acidulated',
    extractFineDryPercent: 78,
    moisturePercent: 6,
    colorLovibond: 2.2,
    colorEbc: 4.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: 3.4,
    bufferingCapacity: null,
    description:
      'Pilsner malt soured with its own lactic bacteria, carrying roughly 1–2 % lactic acid by weight. The Reinheitsgebot-legal way to drop mash pH: about 1 % of the grist moves the mash roughly 0.1 pH down.',
    tags: ['acidulated', 'german', 'ph', 'sauermalz'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-acidulated-malt-4/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-pale-wheat',
    name: 'Pale Wheat Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83,
    moisturePercent: 5.5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 70,
    needsMashing: true,
    diPh: 5.8,
    bufferingCapacity: null,
    description:
      'German malted wheat, high in extract and protein. Builds the dense head and soft body of a weissbier and improves foam anywhere it appears at 5–15 %.',
    tags: ['base', 'wheat', 'german', 'head-retention'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-wheat-malt-pale-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-dark-wheat',
    name: 'Dark Wheat Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5.5,
    colorLovibond: 7.1,
    colorEbc: 17.5,
    diastaticPowerLintner: 22,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: 5.7,
    bufferingCapacity: null,
    description:
      'Wheat malt kilned to an amber color, adding bread-crust depth alongside the usual wheat softness. The obvious lever for a dunkles weissbier.',
    tags: ['wheat', 'german', 'dark'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-wheat-malt-dark-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-carawheat',
    name: 'Carawheat',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 41.9,
    colorEbc: 110,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Caramelized wheat malt. Deepens the color and rounds out the body of wheat beers, and adds a clean caramel note without the grainy edge of barley crystal.',
    tags: ['crystal', 'caramel', 'wheat', 'german'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-carawheat-9/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-chocolate-wheat',
    name: 'Chocolate Wheat Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 5,
    colorLovibond: 340,
    colorEbc: 900,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted wheat malt, husk-free by nature and therefore notably smooth. Gives dark wheat beers and stouts a cocoa depth with very little astringency.',
    tags: ['roasted', 'wheat', 'german', 'chocolate'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-chocolate-wheat-malt-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-rye',
    name: 'Rye Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5.5,
    colorLovibond: 2.9,
    colorEbc: 6.5,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: 5.7,
    bufferingCapacity: null,
    description:
      'Malted rye: spicy, slightly peppery and very high in beta-glucan, so expect a sticky mash above about 20 %. Rice hulls are strongly advised at high percentages.',
    tags: ['base', 'rye', 'german', 'spicy'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-rye-malt-pale-4/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-chocolate-rye',
    name: 'Chocolate Rye Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 245.7,
    colorEbc: 650,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rye roasted to chocolate color, combining cocoa and coffee with rye spice. A distinctive small addition in dark rye beers and roggenbock.',
    tags: ['roasted', 'rye', 'german'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-chocolate-rye-malt-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-beech-smoked',
    name: 'Beech Smoked Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: 5.72,
    bufferingCapacity: null,
    description:
      'Barley malt kilned over beechwood in the Bamberg tradition, retaining full diastatic power. The defining ingredient of Rauchbier at 50–100 % of the grist, or a smoky accent at 5–15 %.',
    tags: ['smoked', 'german', 'rauchbier', 'base'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-beech-smoked-barley-malt-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-oak-smoked-wheat',
    name: 'Oak Smoked Wheat Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5.5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: 5.78,
    bufferingCapacity: null,
    description:
      'Wheat malt smoked over oak, giving a drier and more resinous smoke than beechwood. Traditional in Grätzer/Piwo Grodziskie, where it can be the entire grist.',
    tags: ['smoked', 'wheat', 'german', 'grodziskie'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-oak-smoked-wheat-malt-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-barke-munich',
    name: 'Barke Munich Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4,
    colorLovibond: 7.8,
    colorEbc: 19.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Munich malt off the heritage Barke barley, which yields badly in the field and repays it in the glass: honey and fresh pastry over the usual toasted depth. Weyermann will take it to the whole grist, and a m\u00e4rzen built entirely on it tastes like the reason the variety was kept.',
    tags: ['munich', 'barke', 'heritage', 'german', 'malty', 'marzen'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-barke-munich-malt-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-barke-vienna',
    name: 'Barke Vienna Malt',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 5.5,
    colorLovibond: 3.3,
    colorEbc: 7.5,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The same heirloom barley kilned a shade lighter: gold rather than amber, full-bodied, and with enough enzyme left to convert a grist that leans on it entirely. The choice for a festbier that should taste of malt rather than of color.',
    tags: ['vienna', 'barke', 'heritage', 'german', 'festbier', 'base'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-barke-vienna-malt-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-caraamber',
    name: 'Caraamber',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 26.9,
    colorEbc: 70,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A caramel malt aimed at the red end of amber, deeper than Carared and sweeter with it. Toffee and a fuller head in bocks, red ales and anything that wants its copper to read as color rather than as roast.',
    tags: ['crystal', 'caramel', 'german', 'amber', 'toffee', 'head-retention'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-caraamber-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-carabelge',
    name: 'Carabelge',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 9,
    colorLovibond: 12.7,
    colorEbc: 32.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A pale caramel malt cut for Belgian pale beers: honey and dried fruit, almond behind it, and little of the sticky sweetness a darker crystal brings. It deepens a single or a blonde without taking it amber.',
    tags: ['crystal', 'caramel', 'belgian', 'blonde', 'dubbel', 'honey'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-carabelge-4/',
    provenance: 'spec-sheet',
  },
  {
    id: 'weyermann-special-w',
    name: 'Special W',
    maker: 'Weyermann',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73,
    moisturePercent: 6.5,
    colorLovibond: 113.6,
    colorEbc: 300,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest of the Weyermann caramel malts, and the one that tastes of fruit rather than sugar: plum, raisin and dark crust. It carries a doppelbock or a Belgian dark strong where a roasted malt would only make it bitter.',
    tags: ['crystal', 'caramel', 'dark', 'german', 'doppelbock', 'plum', 'raisin'],
    url: 'https://www.weyermann.de/en-us/product/weyermann-special-w-4/',
    provenance: 'spec-sheet',
  },
];
